import { DEFAULT_LABEL_COLOR } from '@core/models/operation-label.model';

export interface LabelColorStyles {
  /** The label colour itself (dots, swatches). */
  solid: string;
  /** Light pill background. */
  background: string;
  /** Pill border. */
  border: string;
  /** Readable text on the light background. */
  text: string;
}

const HEX = /^#?([0-9a-f]{6})$/i;

function toRgb(hex: string): [number, number, number] {
  const match = HEX.exec((hex || '').trim()) || HEX.exec(DEFAULT_LABEL_COLOR);
  const value = parseInt(match[1], 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function toHex([r, g, b]: number[]): string {
  return '#' + [r, g, b].map(c => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, '0')).join('').toUpperCase();
}

/** Mixes the colour with white (amount 0..1 of the colour kept). */
function tint(rgb: number[], amount: number): string {
  return toHex(rgb.map(c => 255 - (255 - c) * amount));
}

/** Darkens the colour towards black (amount 0..1 of the colour kept). */
function shade(rgb: number[], amount: number): string {
  return toHex(rgb.map(c => c * amount));
}

function luminance(hex: string): number {
  const [r, g, b] = toRgb(hex).map(c => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colours (1..21). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Minimum contrast for chip text (WCAG AA, normal text). */
export const LABEL_TEXT_CONTRAST = 4.5;
/** Neutral border for colours too pale to outline the pill on white. */
const PALE_BORDER = '#C7C7CC';

/**
 * Pill colours derived from the one hex a label stores (PB-5281). The Figma tones
 * (e.g. Cold Chain #4E89C7 -> background #E9EEF4, border #93AECF) are tints of the
 * label colour, so every label gets the same treatment whatever colour is picked.
 */
export function labelColorStyles(color: string): LabelColorStyles {
  const rgb = toRgb(color);
  const background = tint(rgb, 0.12);
  const border = tint(rgb, 0.6);
  // Darken until the text is readable on the pill, so white or pale yellow still works (PRD C-8).
  let amount = 0.72;
  let text = shade(rgb, amount);
  while (contrastRatio(text, background) < LABEL_TEXT_CONTRAST && amount > 0) {
    amount = Math.max(0, amount - 0.04);
    text = shade(rgb, amount);
  }
  return {
    solid: toHex(rgb),
    background,
    border: contrastRatio(border, '#FFFFFF') < 1.3 ? PALE_BORDER : border,
    text
  };
}
